/**
 * Cancel a subscription and refund the unused part of the bundle.
 *
 * The whole calculation is on screen before anything is committed — sessions in
 * the bundle, what one is worth, what the member used, what goes back. Staff
 * are handing real cash across a desk, and a number that only appears after
 * the fact is a number nobody can check.
 *
 * The only thing they type is how many sessions the member attended. Everything
 * else follows from the bundle, so there is nothing to get wrong.
 */
import { useEffect, useState } from 'react';
import { cancelSubscription, previewCancellation } from '../db/operations';
import { classLabel } from '@shared/catalog';
import { formatEgp } from '@shared/money';
import { formatDisplayDate, todayISO } from '@shared/dates';
import {
  cancellationDeadline,
  daysLeftToCancel,
  sessionsInTerm,
  validateCancellation,
  type CancellationQuote,
} from '@shared/cancellations';
import type { SubscriptionRow } from '../db/queries';

export default function CancelDialog({
  row,
  onClose,
}: {
  row: SubscriptionRow;
  onClose: () => void;
}) {
  const today = todayISO();
  const sub = row.subscription;
  const [attended, setAttended] = useState(0);
  const [by, setBy] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [quote, setQuote] = useState<CancellationQuote | null>(null);

  const totalSessions = sessionsInTerm(sub.months);
  const daysLeft = daysLeftToCancel(sub.start_date, today);
  const windowOpen = daysLeft >= 0;

  const errors = validateCancellation(sub, attended, today);
  const canSave = Object.keys(errors).length === 0 && !saving;

  // Priced live so the refund updates as the number is typed. Reads the
  // payments table rather than trusting the row, so a payment taken in another
  // tab cannot leave a stale figure on screen.
  useEffect(() => {
    let cancelled = false;
    previewCancellation(sub.id, attended).then((q) => {
      if (!cancelled) setQuote(q);
    });
    return () => {
      cancelled = true;
    };
  }, [sub.id, attended]);

  async function save() {
    if (!canSave) {
      setError(Object.values(errors)[0] ?? '');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await cancelSubscription({
        subscriptionId: sub.id,
        sessionsAttended: attended,
        receivedBy: by.trim() || 'Front desk',
        today,
      });
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not cancel');
      setSaving(false);
    }
  }

  return (
    <div
      className="no-print fixed inset-0 z-50 flex items-end justify-center bg-black/80 p-4 sm:items-center"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="card max-h-[90vh] w-full max-w-md overflow-y-auto p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="mb-1 text-2xl text-brand">Cancel subscription</h2>
        <p className="mb-5 text-sm text-white/40">
          {classLabel(sub.class_key)}
          {sub.private && ` · Private${sub.private_coach ? ` · ${sub.private_coach}` : ''}`}
          {' · '}
          {formatDisplayDate(sub.start_date)} → {formatDisplayDate(sub.end_date)}
        </p>

        {/* The 14-day rule, stated before anything can be typed. */}
        {!windowOpen ? (
          <div className="mb-5 rounded-btn border border-status-expired/40 bg-status-expiredBg p-4 text-sm text-status-expired">
            <strong className="block">This subscription can no longer be cancelled.</strong>
            The 14-day window closed on{' '}
            {formatDisplayDate(cancellationDeadline(sub.start_date))} — 14 days after the
            start date. The term is the member’s.
          </div>
        ) : (
          <div className="mb-5 rounded-btn border border-status-expiring/40 bg-status-expiringBg p-4 text-sm text-status-expiring">
            {daysLeft === 0 ? (
              <strong>Today is the last day this can be cancelled.</strong>
            ) : (
              <>
                <strong>
                  {daysLeft} {daysLeft === 1 ? 'day' : 'days'} left to cancel.
                </strong>{' '}
                The window closes on{' '}
                {formatDisplayDate(cancellationDeadline(sub.start_date))}.
              </>
            )}
          </div>
        )}

        {windowOpen && (
          <>
            <label className="label" htmlFor="attended">
              Sessions attended
            </label>
            <input
              id="attended"
              type="number"
              min={0}
              max={totalSessions}
              inputMode="numeric"
              className="field tabular text-2xl"
              value={attended}
              autoFocus
              onChange={(e) => {
                setError('');
                const n = Number(e.target.value);
                setAttended(Number.isFinite(n) ? Math.max(0, Math.trunc(n)) : 0);
              }}
            />
            <p className="mt-1 text-xs text-white/30">
              Out of {totalSessions} in this bundle — {sub.months}{' '}
              {sub.months === 1 ? 'month' : 'months'} at 12 sessions a month.
            </p>

            {errors.sessionsAttended && (
              <p className="mt-2 text-sm text-status-expired">{errors.sessionsAttended}</p>
            )}

            {/* The whole sum, shown before the money moves. */}
            {quote && (
              <dl className="mt-5 space-y-2 rounded-btn border border-edge bg-black/30 p-4 text-sm">
                <Line
                  label="Bundle total"
                  value={formatEgp(sub.total_due_piastres, { hideZeroFraction: true })}
                />
                <Line
                  label={`One session (÷ ${quote.totalSessions})`}
                  value={formatEgp(quote.sessionPrice, { hideZeroFraction: true })}
                />
                <Line
                  label={`Used — ${quote.sessionsAttended} ${
                    quote.sessionsAttended === 1 ? 'session' : 'sessions'
                  }`}
                  value={`− ${formatEgp(quote.consumed, { hideZeroFraction: true })}`}
                />
                <Line
                  label="Paid so far"
                  value={formatEgp(row.amountPaid, { hideZeroFraction: true })}
                  muted
                />

                <div className="flex items-baseline justify-between gap-3 border-t border-edge pt-3">
                  <dt className="font-bold uppercase tracking-button text-white/60">
                    Refund
                  </dt>
                  <dd className="tabular text-3xl font-bold text-status-active">
                    {formatEgp(quote.refund, { hideZeroFraction: true })}
                  </dd>
                </div>

                {/* Only possible on a part-paid subscription. */}
                {quote.stillOwed > 0 && (
                  <p className="rounded-btn border border-status-due/40 bg-status-dueBg p-2 text-xs text-status-due">
                    They used more than they paid for — {formatEgp(quote.stillOwed)} is
                    still owed and no refund is due.
                  </p>
                )}
              </dl>
            )}

            <div className="mt-4">
              <label className="label" htmlFor="cancelBy">
                Handled by
              </label>
              <input
                id="cancelBy"
                className="field"
                value={by}
                onChange={(e) => setBy(e.target.value)}
                placeholder="Your name"
              />
            </div>
          </>
        )}

        {error && (
          <p className="mt-4 rounded-btn border border-status-expired/40 bg-status-expiredBg p-3 text-sm text-status-expired">
            {error}
          </p>
        )}

        <div className="mt-6 flex gap-3">
          <button onClick={onClose} className="btn-ghost flex-1">
            {windowOpen ? 'Keep it' : 'Close'}
          </button>
          {windowOpen && (
            <button
              onClick={save}
              disabled={!canSave}
              className="min-h-touch flex-1 rounded-btn bg-status-expired px-4 text-sm font-bold
                         uppercase tracking-button text-paper disabled:opacity-30"
            >
              {saving
                ? 'Cancelling…'
                : quote && quote.refund > 0
                  ? `Refund ${formatEgp(quote.refund, { hideZeroFraction: true })}`
                  : 'Cancel subscription'}
            </button>
          )}
        </div>

        {windowOpen && (
          <p className="mt-3 text-center text-xs leading-relaxed text-white/25">
            This cannot be undone. The refund is recorded as a payment out, so it shows in
            today’s takings and in both portals.
          </p>
        )}
      </div>
    </div>
  );
}

function Line({
  label,
  value,
  muted = false,
}: {
  label: string;
  value: string;
  muted?: boolean;
}) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className={muted ? 'text-white/30' : 'text-white/50'}>{label}</dt>
      <dd className={`tabular font-semibold ${muted ? 'text-white/30' : ''}`}>{value}</dd>
    </div>
  );
}
